/* ============================================================
   Toee Tire — size-level stock lookup (dealer recruitment funnel).
   Reusable proof component: given a size (three cascading tags,
   e.g. width_215+aspect_50+diameter_17), shows how many units are
   available per warehouse for that SIZE — not per individual SKU,
   since one size is usually 5-6 SKUs of which only some are in
   stock (see docs/superpowers/plans/2026-08-26-dealer-recruitment-
   funnel.md Task 1). Two-step lookup:
     1. GET /collections/<handle>/<tags>?view=size-stock
        (templates/collection.size-stock.liquid) — aggregates the
        matching products server-side and returns up to 6 in-stock
        variant ids.
     2. Fetch each variant's existing per-variant warehouse endpoint
        (assets/product-warehouse-inventory.js's PROXY_URL) in
        parallel and sum the per-warehouse counts across variants.
   Exposed as window.ToeeSizeStock (and CommonJS for tests), mirroring
   the assets/size-combo-index.js module pattern.
   ============================================================ */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  if (root && typeof root === 'object') {
    root.ToeeSizeStock = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var WAREHOUSE_PROXY_URL = '/apps/paymentstatussync/delivery/warehouse-inventory';
  var MAX_VARIANTS = 6;

  function cleanUrl(url) {
    return String(url == null ? '' : url).replace(/\/$/, '');
  }

  function joinTags(collectionUrl, tagHandles) {
    var base = cleanUrl(collectionUrl);
    var tags = (tagHandles || []).filter(function (h) { return !!h; });
    return base + (tags.length ? '/' + tags.join('+') : '');
  }

  function buildSizeStockUrl(collectionUrl, tagHandles) {
    return joinTags(collectionUrl, tagHandles) + '?view=size-stock';
  }

  function parseSizeStockResponse(json) {
    var src = json || {};
    var variants = Array.isArray(src.variants) ? src.variants : [];
    return {
      skuCount: src.sku_count || 0,
      inStockCount: src.in_stock_count || 0,
      totalUnits: src.total_units || 0,
      variants: variants.slice(0, MAX_VARIANTS)
    };
  }

  // Sum each warehouse's quantity across the (up to 6) per-variant fetches.
  // A warehouse is "unavailable" only when no *successful* variant fetch
  // ever reported it — otherwise we show the sum from whichever variants
  // did succeed, rather than discarding a correct partial figure. If any
  // fetch failed, that sum can only ever be a floor (the failed variant
  // might stock this warehouse too), so those rows are additionally
  // flagged `partial` so the UI can render "at least N" instead of a bare,
  // possibly-understated number.
  // `fallbackLabels` (optional) mirrors product-warehouse-inventory.js's
  // data-fallback-warehouses convention: it fixes the row order and makes
  // sure a warehouse the API never mentioned still gets a row — a
  // confidently-known 0 when every variant succeeded and simply never
  // listed it, or `unavailable` when something failed and this warehouse
  // was never actually reported by any successful variant.
  function aggregateWarehouses(variantResults, fallbackLabels) {
    var results = variantResults || [];
    var anyFailure = false;
    var order = [];
    var byKey = {};

    results.forEach(function (r) {
      var inv = r && r.ok && r.body && r.body.success !== false && r.body.productWarehouseInventory;
      var rows = inv && Array.isArray(inv.warehouses) ? inv.warehouses : null;
      if (!rows) {
        anyFailure = true;
        return;
      }
      rows.forEach(function (w) {
        var label = w && w.warehouseLabel;
        if (!label) return;
        var key = label.toLowerCase();
        if (!byKey[key]) {
          byKey[key] = { label: label, quantity: 0 };
          order.push(key);
        }
        var qty = w.quantity;
        if (typeof qty === 'number' && qty > 0) byKey[key].quantity += qty;
      });
    });

    function row(label, known) {
      if (known) {
        // Reported by at least one successful variant: show the sum. If
        // something else failed, flag it as a floor rather than an exact
        // count.
        return { label: known.label, quantity: known.quantity, available: true, partial: anyFailure };
      }
      // Never reported by any successful variant. With no failures at all
      // that's a confidently-known zero; with a failure, we can't tell
      // whether the missing variant would have stocked it, so it stays
      // unavailable instead of a possibly-wrong zero.
      return { label: label, quantity: 0, available: !anyFailure, partial: false };
    }

    var fallback = (fallbackLabels || []).filter(Boolean);
    if (fallback.length === 0) {
      return order.map(function (key) { return row(byKey[key].label, byKey[key]); });
    }

    var out = [];
    var seen = {};
    fallback.forEach(function (label) {
      var key = label.toLowerCase();
      seen[key] = true;
      out.push(row(label, byKey[key]));
    });
    // Any warehouse the API returned that isn't in the configured fallback
    // list still gets shown rather than dropped.
    order.forEach(function (key) {
      if (seen[key]) return;
      out.push(row(byKey[key].label, byKey[key]));
    });
    return out;
  }

  // Fetches each variant's warehouse breakdown in parallel (capped at 6).
  // Each request catches its own failure so one bad variant never blocks
  // the others — Promise.all only waits, it never short-circuit-rejects
  // here because every branch already resolves to { ok, body }.
  function fetchAndAggregate(variantIds, fetchImpl, fallbackLabels) {
    var doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
    var ids = (variantIds || []).slice(0, MAX_VARIANTS);
    if (!doFetch || ids.length === 0) return Promise.resolve([]);

    var requests = ids.map(function (id) {
      var url = WAREHOUSE_PROXY_URL + '?variant_id=' + encodeURIComponent(id);
      return doFetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          return response.json().catch(function () { return null; }).then(function (body) {
            return { ok: response.ok, body: body };
          });
        })
        .catch(function () {
          return { ok: false, body: null };
        });
    });

    return Promise.all(requests).then(function (results) {
      return aggregateWarehouses(results, fallbackLabels);
    });
  }

  function fallbackWarehouseLabels(panel) {
    var raw = (panel && panel.getAttribute('data-fallback-warehouses')) || '';
    return raw.split(/[|\n]/).map(function (l) { return l.trim(); }).filter(Boolean);
  }

  function text(panel, name, fallback) {
    return (panel && panel.getAttribute('data-i18n-' + name)) || fallback || '';
  }

  function renderActions(panel, opts) {
    var actions = panel.querySelector('[data-toee-size-stock-actions]');
    if (!actions) return;
    actions.hidden = false;

    var primary = actions.querySelector('[data-toee-size-stock-primary]');
    if (primary) {
      primary.href = panel.getAttribute('data-signup-url') || '/pages/tire-wholesale-toronto';
      primary.textContent = panel.getAttribute('data-signup-label') || '';
    }

    var secondary = actions.querySelector('[data-toee-size-stock-secondary]');
    if (secondary) {
      if (opts && opts.showSecondary) {
        secondary.hidden = false;
        secondary.href = opts.collectionUrl || '#';
        var tpl = text(panel, 'view-tires', 'View the {{ count }} tires in this size');
        secondary.textContent = tpl.replace('{{ count }}', String(opts.count));
      } else {
        secondary.hidden = true;
      }
    }
  }

  function bodyOf(panel) {
    return panel.querySelector('[data-toee-size-stock-body]');
  }

  function showMessage(panel, msg, isError) {
    var body = bodyOf(panel);
    if (!body) return;
    body.textContent = '';
    var p = document.createElement('p');
    p.className = 'tt-size-stock-msg' + (isError ? ' tt-size-stock-msg--error' : '');
    p.textContent = msg;
    body.appendChild(p);
  }

  function showLoading(panel) {
    panel.hidden = false;
    showMessage(panel, text(panel, 'loading', 'Checking warehouse inventory…'));
    var actions = panel.querySelector('[data-toee-size-stock-actions]');
    if (actions) actions.hidden = true;
  }

  function showError(panel) {
    showMessage(panel, text(panel, 'error', 'Inventory temporarily unavailable'), true);
    renderActions(panel, { showSecondary: false });
  }

  function showOutOfStock(panel) {
    showMessage(panel, text(panel, 'out-of-stock', 'This size is currently out of stock.'));
    var contactUrl = panel.getAttribute('data-contact-url');
    if (contactUrl) {
      var body = bodyOf(panel);
      var link = document.createElement('a');
      link.className = 'tt-viewall';
      link.href = contactUrl;
      link.textContent = panel.getAttribute('data-contact-label') || text(panel, 'contact', 'Contact us');
      if (body) body.appendChild(link);
    }
    renderActions(panel, { showSecondary: false });
  }

  function renderResult(panel, data, warehouses, collectionUrl) {
    var body = bodyOf(panel);
    if (!body) return;
    body.textContent = '';

    var list = document.createElement('ul');
    list.className = 'tt-size-stock-list';
    warehouses.forEach(function (w) {
      var row = document.createElement('li');
      row.className = 'tt-size-stock-row';
      var name = document.createElement('span');
      name.className = 'tt-size-stock-name';
      name.textContent = w.label;
      var qty = document.createElement('span');
      qty.className = 'tt-size-stock-qty';
      if (!w.available) {
        qty.textContent = text(panel, 'warehouse-unavailable', 'Unavailable');
      } else if (w.partial) {
        qty.textContent = text(panel, 'at-least-units', 'At least {{ count }}').replace('{{ count }}', String(w.quantity));
      } else {
        qty.textContent = String(w.quantity);
      }
      row.appendChild(name);
      row.appendChild(qty);
      list.appendChild(row);
    });
    body.appendChild(list);

    var total = document.createElement('p');
    total.className = 'tt-size-stock-total';
    total.textContent = text(panel, 'total-units', '{{ count }} units in stock').replace('{{ count }}', String(data.totalUnits));
    body.appendChild(total);

    var promise = document.createElement('p');
    promise.className = 'tt-size-stock-promise';
    promise.textContent = text(panel, 'promise', '');
    body.appendChild(promise);

    renderActions(panel, { showSecondary: true, collectionUrl: collectionUrl, count: data.skuCount });
  }

  // DOM entry point: called from assets/toee-home.js on submit when the
  // section's result_mode setting is "inline". `panel` is the
  // [data-toee-size-stock] container rendered by
  // sections/toee-size-search.liquid; `collectionUrl` is the active mode's
  // target collection URL; `tagHandles` are the selected size tag handles.
  function run(panel, collectionUrl, tagHandles) {
    if (!panel) return;
    showLoading(panel);

    // The secondary CTA links to the plain (un-viewed) tag-filtered collection
    // page — same tag path as the JSON view, just without ?view=size-stock.
    var plainCollectionUrl = joinTags(collectionUrl, tagHandles);
    var url = buildSizeStockUrl(collectionUrl, tagHandles);
    // No Accept header here: Shopify's storefront ignores `?view=` on a
    // request that carries `Accept: application/json` and serves its own
    // built-in collection JSON instead of templates/collection.size-stock.
    // liquid. That response has no sku_count/in_stock_count/variants, so
    // parseSizeStockResponse silently zeroes out and this always renders
    // "out of stock" — looks like an inventory bug, is actually this header.
    // Do not add it back.
    fetch(url, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : Promise.reject(res.status); })
      .then(function (json) {
        var data = parseSizeStockResponse(json);
        if (!data.inStockCount || data.variants.length === 0) {
          showOutOfStock(panel);
          return;
        }
        var apiEnabled = panel.getAttribute('data-inventory-api-enabled') === 'true';
        if (!apiEnabled) {
          renderResult(panel, data, [], plainCollectionUrl);
          return;
        }
        var variantIds = data.variants.map(function (v) { return v.id; });
        fetchAndAggregate(variantIds, null, fallbackWarehouseLabels(panel)).then(function (warehouses) {
          renderResult(panel, data, warehouses, plainCollectionUrl);
        });
      })
      .catch(function () {
        showError(panel);
      });
  }

  return {
    joinTags: joinTags,
    buildSizeStockUrl: buildSizeStockUrl,
    parseSizeStockResponse: parseSizeStockResponse,
    aggregateWarehouses: aggregateWarehouses,
    fetchAndAggregate: fetchAndAggregate,
    fallbackWarehouseLabels: fallbackWarehouseLabels,
    run: run
  };
});
